import { memo, useMemo, forwardRef } from "react";
import { useQuery } from "@tanstack/react-query";
import LazySection from "./LazySection";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { useHomeCategories } from "@/hooks/useCategories";
import ProductCard from "./ProductCard";
import { useSiteTheme } from "@/hooks/useSiteTheme";
import { Skeleton } from "@/components/ui/skeleton";
// Inline SVG arrow to avoid pulling lucide-react icons chunk
const ArrowRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
);

const QUERY_TIMEOUT_MS = 8000;

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> =>
  Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      globalThis.setTimeout(() => reject(new Error("home_category_sections_timeout")), timeoutMs);
    }),
  ]);

interface HomeCat {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
}

const PRODUCT_COLS = "id, name, price, offer_price, image, seo_slug, rating, stock, is_active, category_id";

const ProductSkeleton = () => (
  <div className="space-y-3">
    <div className="aspect-[3/4] w-full bg-muted animate-pulse rounded" />
    <div className="space-y-2 px-1">
      <div className="h-4 w-3/4 bg-muted animate-pulse rounded" />
      <div className="h-4 w-1/2 bg-muted animate-pulse rounded" />
    </div>
  </div>
);

const fetchProductsForCategories = async (cats: HomeCat[]): Promise<{ category: HomeCat; products: any[] }[]> => {
  if (!cats || cats.length === 0) return [];

  try {
    const catIds = cats.map((c) => c.id);

    const { data: pcData } = await withTimeout(
      Promise.resolve(
        supabase
          .from("product_categories")
          .select("product_id, category_id, sort_order")
          .in("category_id", catIds)
          .order("sort_order", { ascending: true })
      ),
      QUERY_TIMEOUT_MS,
    );

    const allProductIds = Array.from(new Set((pcData || []).map((pc) => pc.product_id)));

    const { data: directProducts } = await withTimeout(
      Promise.resolve(
        supabase
          .from("products")
          .select(PRODUCT_COLS)
          .in("category_id", catIds)
          .eq("is_active", true)
      ),
      QUERY_TIMEOUT_MS,
    );

    let mappedProducts: any[] = [];
    if (allProductIds.length > 0) {
      const chunks: string[][] = [];
      for (let i = 0; i < allProductIds.length; i += 100) {
        chunks.push(allProductIds.slice(i, i + 100));
      }
      const results = await withTimeout(
        Promise.all(
          chunks.map((chunk) =>
            Promise.resolve(supabase.from("products").select(PRODUCT_COLS).in("id", chunk).eq("is_active", true))
          )
        ),
        QUERY_TIMEOUT_MS,
      );
      mappedProducts = results.flatMap((r) => r.data || []);
    }

    const productMap = new Map<string, any>();
    mappedProducts.forEach((p) => productMap.set(p.id, p));
    (directProducts || []).forEach((p) => productMap.set(p.id, p));

    const catProductMap = new Map<string, { ids: Set<string>; sorted: string[] }>();
    catIds.forEach((id) => catProductMap.set(id, { ids: new Set(), sorted: [] }));

    (pcData || []).forEach((pc) => {
      const entry = catProductMap.get(pc.category_id);
      if (entry && !entry.ids.has(pc.product_id)) {
        entry.ids.add(pc.product_id);
        entry.sorted.push(pc.product_id);
      }
    });

    (directProducts || []).forEach((p) => {
      if (p.category_id) {
        const entry = catProductMap.get(p.category_id);
        if (entry && !entry.ids.has(p.id)) {
          entry.ids.add(p.id);
          entry.sorted.push(p.id);
        }
      }
    });

    return cats
      .map((cat) => {
        const entry = catProductMap.get(cat.id);
        if (!entry || entry.sorted.length === 0) return null;
        const products = entry.sorted
          .map((id) => productMap.get(id))
          .filter(Boolean)
          .slice(0, 8);
        return { category: cat, products };
      })
      .filter(Boolean) as { category: HomeCat; products: any[] }[];
  } catch (error) {
    console.error("home category sections load failed", error);
    return [];
  }
};

interface CategorySectionProps {
  category: HomeCat;
  products: any[];
  index: number;
}

const CategorySection = memo(({ category, products, index }: CategorySectionProps) => {
  const { theme } = useSiteTheme();

  if (products.length === 0) return null;

  const bgColor = index % 2 === 0 ? theme.sectionBg : theme.sectionAltBg;
  const displayProducts = products.slice(0, 8);
  const hasMore = products.length > 8;

  return (
    <section className="py-10 md:py-20" style={{ backgroundColor: `hsl(${bgColor})`, contain: "layout style" }}>
      <div className="container mx-auto px-4" style={{ maxWidth: 1440 }}>
        <header className="text-center mb-8 md:mb-14">
          <h2
            className="text-2xl md:text-4xl lg:text-5xl"
            style={{
              color: `hsl(${theme.cardText})`,
              fontFamily: "var(--design-font-heading)",
              fontWeight: 700,
            }}
          >
            {category.name}
          </h2>
          <div
            className="w-20 h-[2px] mx-auto mt-4 rounded-full"
            style={{
              background: `linear-gradient(90deg, transparent, hsl(${theme.cardAccent}), transparent)`,
            }}
          />
        </header>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {displayProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {hasMore && (
          <div className="text-center mt-8 md:mt-12">
            <Link
              to={`/category/${category.slug}`}
              className="inline-flex items-center gap-2 px-8 py-3 text-xs tracking-[0.2em] uppercase border transition-all duration-300 hover:scale-105"
              style={{
                borderColor: `hsl(${theme.btnBg})`,
                color: `hsl(${theme.cardText})`,
                borderRadius: theme.btnRadius,
                fontFamily: "var(--design-font-ui)",
                fontWeight: 700,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `hsl(${theme.btnBg})`;
                e.currentTarget.style.color = `hsl(${theme.btnText})`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.color = `hsl(${theme.cardText})`;
              }}
            >
              View All <ArrowRightIcon />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
});

CategorySection.displayName = "CategorySection";

interface HomeCategorySectionsProps {
  enabled?: boolean;
}

const HomeCategorySections = memo(forwardRef<HTMLDivElement, HomeCategorySectionsProps>(({ enabled = true }, ref) => {
  // ★ Reuse shared category cache instead of separate fetch
  const { data: homeCats = [], isLoading: catsLoading } = useHomeCategories(enabled);
  
  const homeCatList = useMemo(
    () => homeCats.map((c) => ({ id: c.id, name: c.name, slug: c.slug, sort_order: 0 })),
    [homeCats]
  );

  const { data = [], isLoading: productsLoading } = useQuery<{ category: HomeCat; products: any[] }[]>({
    queryKey: ["home-category-sections", homeCatList.map((c) => c.id).join(",")],
    queryFn: () => fetchProductsForCategories(homeCatList),
    staleTime: 3 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: enabled && homeCatList.length > 0,
  });

  const isLoading = catsLoading || productsLoading;

  if (isLoading) {
    return (
      <section className="py-10 md:py-20" style={{ minHeight: '500px' }}>
        <div className="container mx-auto px-4" style={{ maxWidth: 1440 }}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <ProductSkeleton key={i} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (data.length === 0) return null;

  return (
    <div ref={ref}>
      {data.map((item, index) => (
        <LazySection key={item.category.id} height="500px">
          <CategorySection
            category={item.category}
            products={item.products}
            index={index}
          />
        </LazySection>
      ))}
    </div>
  );
}));

HomeCategorySections.displayName = "HomeCategorySections";

export default HomeCategorySections;
